import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { ScopeButton } from '@monorepo/shared-lib';
import { Spin } from 'antd';
import { ReactComponent as CloseIcon } from 'assets/images/icons/crossIcon.svg';
import { ReactComponent as EditIcon } from 'assets/images/icons/editIcon.svg';
import { FormInput } from 'components/Custom/inputs';
import { StyledPopconfirm } from 'components/Custom/Popconfirm';
import moment from 'moment';
import { formatDisplayDate } from 'i18n/calendarLabels';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getExcelTransactionAccountTypeLabel } from 'utils/display-enum-label';
import {
  deleteChartOfAccountsTemplateRequest,
  fetchChartOfAccountsTemplatesRequest,
  updateChartOfAccountsTemplateNameRequest,
} from 'store/chart-of-accounts/actions';
import {
  getChartOfAccountTemplates,
  getChartOfAccountsLoading,
} from 'store/chart-of-accounts/selectors';
import styled from 'styled-components';

const Wrapper = styled.div`
  background: var(--color-layout-bg, #e9f6f7);
  font-family: Inter, sans-serif;

  .template-name-title {
    font-family: Inter;
    font-weight: 600;
    font-size: 15px;
    line-height: 60px;
    letter-spacing: 0%;
    color: var(--color-text-dark, #042a2a);
  }

  .form-input {
    width: 100%;
    max-width: 370px;
  }

  .input-container {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 32px;
  }

  .input-wrapper {
    flex: 1;
    max-width: 370px;
  }

  .templates-section {
    margin-top: 20px;
  }

  .templates-title {
    font-family: Inter;
    font-weight: 400;
    font-size: 18px;
    color: var(--color-text-dark, #042a2a);
    margin-bottom: 16px;
  }

  .template-list {
    margin-bottom: 32px;
  }

  .template-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 0;
    border-bottom: 1px solid var(--color-border, #dbeae3);
    cursor: pointer;
    transition: background-color 0.2s;
  }

  .template-item:hover {
    background-color: #f0f9fa;
  }

  .template-name {
    font-family: Inter;
    font-weight: 700;
    font-size: 16px;
    color: var(--color-text-dark, #042a2a);
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-right: 8px;
  }

  .template-meta {
    font-family: Inter;
    font-weight: 400;
    font-size: 12px;
    color: var(--color-text-muted, #5a6a6a);
    flex-shrink: 0;
  }

  .template-info {
    display: flex;
    flex-direction: column;
    flex: 1;
    margin-right: 8px;
  }

  .template-date {
    font-family: Inter;
    font-weight: 400;
    font-size: 12px;
    color: var(--color-text-muted, #5a6a6a);
  }

  .actions {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .edit-icon {
    color: var(--color-text-muted, #5a6a6a);
    width: 19px;
    height: 19px;
    cursor: pointer;
    transition: color 0.2s;
  }

  .edit-icon:hover {
    color: var(--color-primary, #41aaaf);
  }

  .eye-icon {
    color: var(--color-text-muted, #5a6a6a);
    width: 19px;
    height: 19px;
    font-size: 20px;
    cursor: pointer;
    transition: color 0.2s;
  }

  .eye-icon:hover {
    color: var(--color-primary, #41aaaf);
  }

  .close-icon {
    font-weight: 400;
    width: 17px;
    height: 17px;
    color: var(--color-text-muted, #5a6a6a);
    cursor: pointer;
    transition: color 0.2s;
  }

  .close-icon:hover {
    color: var(--color-primary, #41aaaf);
  }

  .accounts-section {
    background-color: var(--color-surface, #f8fbfb);
    border-left: 2px solid var(--color-primary, #41aaaf);
    margin-left: 4px;
    margin-top: 4px;
    padding: 4px 8px;
    border-radius: 0 4px 4px 0;
  }

  .accounts-list {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  .account-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 4px 0;
    border-bottom: 1px solid #e8f0f0;
  }

  .account-item:last-child {
    border-bottom: none;
  }

  .account-name {
    font-family: Inter;
    font-weight: 500;
    font-size: 13px;
    color: var(--color-text-dark, #042a2a);
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-right: 8px;
  }

  .account-type {
    font-family: Inter;
    font-weight: 400;
    font-size: 12px;
    color: var(--color-primary, #41aaaf);
    flex-shrink: 0;
  }

  .no-accounts {
    font-family: Inter;
    font-weight: 400;
    font-size: 13px;
    color: var(--color-text-muted, #5a6a6a);
    font-style: italic;
  }

  .scope-button {
    width: 100%;
    max-width: 370px;
    margin: 0 auto;
    display: block;
    margin-top: 24px;
  }

  .edit-controls {
    display: flex;
    gap: 8px;
    align-items: center;
  }

  .save-icon,
  .cancel-icon {
    width: 20px;
    height: 20px;
    cursor: pointer;
    transition: color 0.2s;
  }

  .save-icon {
    color: #52c41a;
  }

  .save-icon:hover {
    color: #389e0d;
  }

  .cancel-icon {
    color: #ff4d4f;
  }

  .cancel-icon:hover {
    color: #cf1322;
  }
`;

interface CreateCoaTemplateProps {
  /** When "create", show name input + create button; when "manage", show only "My Templates". */
  mode: 'create' | 'manage';
  onSaveTemplate?: (templateName: string) => void;
}

export const CreateCoaTemplate: React.FC<CreateCoaTemplateProps> = ({
  mode,
  onSaveTemplate,
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const [templateName, setTemplateName] = React.useState('');
  const [isEditMode, setIsEditMode] = React.useState(false);
  const [editingTemplateId, setEditingTemplateId] = React.useState<
    string | null
  >(null);
  const [expandedTemplates, setExpandedTemplates] = React.useState<Set<string>>(
    () => new Set()
  );
  const templates = useSelector(getChartOfAccountTemplates) || [];
  const loading = useSelector(getChartOfAccountsLoading);

  React.useEffect(() => {
    // Ensure templates are loaded when the drawer is opened in manage mode
    if (mode === 'manage') {
      dispatch(fetchChartOfAccountsTemplatesRequest());
    }
  }, [dispatch, mode]);

  const toggleTemplateExpansion = (templateId: string) => {
    setExpandedTemplates((prev) => {
      const next = new Set(prev);
      if (next.has(templateId)) {
        next.delete(templateId);
      } else {
        next.add(templateId);
      }
      return next;
    });
  };

  const handleTemplateClick = (
    e: React.MouseEvent<HTMLDivElement>,
    templateId: string
  ) => {
    const target = e.target as HTMLElement;
    if (target.closest('.actions')) {
      return;
    }
    toggleTemplateExpansion(templateId);
  };

  const handleCreateTemplate = () => {
    const trimmed = templateName.trim();
    if (!trimmed || !onSaveTemplate) {
      return;
    }
    onSaveTemplate(trimmed);
  };

  const handleEditTemplate = (templateId: string, name: string) => {
    setIsEditMode(true);
    setEditingTemplateId(templateId);
    setTemplateName(name);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditingTemplateId(null);
    setTemplateName('');
  };

  const handleSaveEdit = () => {
    const trimmed = templateName.trim();
    if (!trimmed || !editingTemplateId) return;
    dispatch(
      updateChartOfAccountsTemplateNameRequest(editingTemplateId, trimmed)
    );
    setIsEditMode(false);
    setEditingTemplateId(null);
    setTemplateName('');
  };

  return (
    <Wrapper>
      {mode === 'create' && (
        <div>
          <label htmlFor="coa-template-name" className="template-name-title">
            {t('organisation.template-name')}
          </label>
          <div className="input-container">
            <div className="input-wrapper">
              <FormInput
                id="coa-template-name"
                className="form-input"
                placeholder={t('organisation.template-name')}
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
              />
            </div>
          </div>
        </div>
      )}

      {mode === 'manage' && isEditMode && (
        <div>
          <label
            htmlFor="coa-template-name-edit"
            className="template-name-title"
          >
            {t('organisation.template-name')}
          </label>
          <div className="input-container">
            <div className="input-wrapper">
              <FormInput
                id="coa-template-name-edit"
                className="form-input"
                placeholder={t('organisation.template-name')}
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
              />
            </div>
            <div className="edit-controls">
              <svg
                className="save-icon"
                viewBox="0 0 24 24"
                fill="currentColor"
                onClick={handleSaveEdit}
              >
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
              </svg>
              <svg
                className="cancel-icon"
                viewBox="0 0 24 24"
                fill="currentColor"
                onClick={handleCancelEdit}
              >
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
              </svg>
            </div>
          </div>
        </div>
      )}

      {mode === 'manage' && (
        <div className="templates-section">
          <div className="templates-title">{t('organisation.my-templates')}</div>
          {loading ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 24,
                gap: 8,
              }}
            >
              <Spin size="small" />
              <span
                style={{
                  fontFamily: 'Inter',
                  fontSize: 13,
                  color: 'var(--color-text-dark, #042a2a)',
                  fontWeight: 500,
                }}
              >
                {t('organisation.loading-templates')}
              </span>
            </div>
          ) : !templates || templates.length === 0 ? (
            <div
              style={{
                fontFamily: 'Inter',
                fontWeight: 400,
                fontSize: 13,
                color: 'var(--color-text-muted, #5a6a6a)',
                fontStyle: 'italic',
                padding: '4px 0',
              }}
            >
              {t('organisation.no-templates-available-yet')}
            </div>
          ) : (
            <div className="template-list">
              {templates.map((tpl: any) => {
                const templateId = tpl.id || tpl.name;
                const accounts: any[] = Array.isArray(tpl.accounts)
                  ? tpl.accounts
                  : [];
                const isExpanded = expandedTemplates.has(templateId);
                const createdLabel = tpl.createdDate
                  ? formatDisplayDate(tpl.createdDate, 'DD/MM/YYYY', i18n.language)
                  : null;

                return (
                  <div key={templateId}>
                    <div
                      className="template-item"
                      onClick={(e) => handleTemplateClick(e, templateId)}
                    >
                      <div className="template-info">
                        <span className="template-name">
                          {tpl.name}
                          {accounts.length ? ` (${accounts.length})` : ''}
                        </span>
                        {createdLabel && (
                          <span className="template-date">
                            {t('organisation.template-created-date', {
                              date: createdLabel,
                            })}
                          </span>
                        )}
                      </div>
                      <div className="actions">
                        {isExpanded ? (
                          <EyeInvisibleOutlined
                            className="eye-icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleTemplateExpansion(templateId);
                            }}
                          />
                        ) : (
                          <EyeOutlined
                            className="eye-icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleTemplateExpansion(templateId);
                            }}
                          />
                        )}
                        <EditIcon
                          className="edit-icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!tpl.id) return;
                            handleEditTemplate(templateId, tpl.name);
                          }}
                        />
                        <StyledPopconfirm
                          title={t('organisation.delete-template-popconfirm-title')}
                          description={t('organisation.delete-this-template-description')}
                          onConfirm={() => {
                            const idToDelete = tpl.id;
                            if (!idToDelete) return;
                            dispatch(
                              deleteChartOfAccountsTemplateRequest(idToDelete)
                            );
                          }}
                          okText={t('common.delete')}
                          cancelText={t('common.cancel')}
                          placement="left"
                        >
                          <CloseIcon
                            className="close-icon"
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                          />
                        </StyledPopconfirm>
                      </div>
                    </div>
                    {isExpanded && (
                      <div className="accounts-section">
                        {accounts.length > 0 ? (
                          <ul className="accounts-list">
                            {accounts.map((acc: any, index: number) => (
                              <li
                                key={acc.id || `${templateId}-${index}`}
                                className="account-item"
                              >
                                <span className="account-name">
                                  {acc.name || '-'}
                                </span>
                                {acc.type && (
                                  <span className="account-type">
                                    {getExcelTransactionAccountTypeLabel(
                                      acc.type,
                                      t
                                    )}
                                  </span>
                                )}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <div className="no-accounts">
                            {t('organisation.no-accounts-for-template')}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {mode === 'create' && (
        <ScopeButton
          type="primary"
          className="scope-button"
          onClick={handleCreateTemplate}
          disabled={!templateName.trim()}
        >
          {t('organisation.create-template')}
        </ScopeButton>
      )}
    </Wrapper>
  );
};
